/**
 * <pvz-auto-contrast>
 *
 * 商品カードの商品名・価格 (.pvz-pcard__info) の文字色を、下に重なる画像の明るさに合わせて
 * 黒 / 白に切り替える。画像が暗ければ白文字 (is-light クラス) にする。
 *
 * カード内の画像 (object-fit: contain で正方形に収めている) を小さなキャンバスに同じ配置で描き、
 * この要素が重なっている範囲の平均の明るさを見る。
 * 画像は店舗ドメイン (/cdn/shop/files/...) から読むので同一オリジンで色を読める。
 * 読めない場合 (別ドメインの画像など) は何もしない = 既定の黒文字のまま。
 *
 * Markup: snippets/pvz-product-card.liquid
 *   <a class="pvz-pcard"><img class="pvz-pcard__image">…<pvz-auto-contrast class="pvz-pcard__info">…</pvz-auto-contrast></a>
 */
if (!customElements.get('pvz-auto-contrast')) {
  customElements.define(
    'pvz-auto-contrast',
    class PvzAutoContrast extends HTMLElement {
      // これより明るければ黒文字、暗ければ白文字 (0〜255)
      static THRESHOLD = 150;

      connectedCallback() {
        this.card = this.closest('.pvz-pcard');
        this.img = this.card && this.card.querySelector('img.pvz-pcard__image');
        if (!this.img) return;

        if (this.img.complete && this.img.naturalWidth) {
          this.update();
        } else {
          this.img.addEventListener('load', () => this.update(), { once: true });
        }
      }

      update() {
        const luminance = this.measure();
        if (luminance === null) return;
        this.classList.toggle('is-light', luminance < PvzAutoContrast.THRESHOLD);
      }

      // この要素が重なっている範囲の平均輝度。読めなければ null
      measure() {
        const cardRect = this.card.getBoundingClientRect();
        const infoRect = this.getBoundingClientRect();
        const width = Math.round(cardRect.width);
        const height = Math.round(cardRect.height);
        if (!width || !height || !infoRect.width || !infoRect.height) return null;

        // 計算を軽くするため 1/4 に縮めて描く
        const scale = 0.25;
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(width * scale));
        canvas.height = Math.max(1, Math.round(height * scale));
        const ctx = canvas.getContext('2d', { willReadFrequently: true });

        // カードの背景 (白) の上に、object-fit: contain と同じ位置・大きさで画像を描く
        ctx.fillStyle = '#fff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        const nw = this.img.naturalWidth;
        const nh = this.img.naturalHeight;
        const fit = Math.min(width / nw, height / nh);
        const dw = nw * fit;
        const dh = nh * fit;
        ctx.drawImage(this.img, ((width - dw) / 2) * scale, ((height - dh) / 2) * scale, dw * scale, dh * scale);

        const x = Math.floor((infoRect.left - cardRect.left) * scale);
        const y = Math.floor((infoRect.top - cardRect.top) * scale);
        const w = Math.max(1, Math.round(infoRect.width * scale));
        const h = Math.max(1, Math.round(infoRect.height * scale));

        let data;
        try {
          data = ctx.getImageData(x, y, w, h).data;
        } catch (error) {
          // 別オリジンの画像はキャンバスから色を読めない
          return null;
        }

        let sum = 0;
        for (let i = 0; i < data.length; i += 4) {
          sum += 0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2];
        }
        const luminance = sum / (data.length / 4);

        // SOLD OUT などで画像を半透明にしているときは、白いカード背景と混ざった明るさで判定する
        const opacity = parseFloat(getComputedStyle(this.img).opacity);
        return luminance * opacity + 255 * (1 - opacity);
      }
    }
  );
}
